import { useLayoutEffect, useRef, useState } from "react";
import { X, Plus } from "lucide-react";
import { ProvTag } from "@/components/Brand";

export const Field = ({ label, prov, children, hint }) => (
  <label className="block space-y-1">
    <span className="flex items-center gap-2"><span className="eyebrow">{label}</span><ProvTag prov={prov} /></span>
    {children}
    {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

export const inputCls = "w-full rounded border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-sky-400 disabled:bg-slate-50 disabled:text-slate-500";

// Text box that grows to show its full content.
export function AutoText({ className = "", rows = 1, ...p }) {
  const ref = useRef(null);
  const fit = () => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + 2}px`; } };
  useLayoutEffect(fit, [p.value]);
  useLayoutEffect(() => {
    const ro = new ResizeObserver(fit);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return <textarea ref={ref} rows={rows} {...p} className={`${className} resize-none overflow-hidden whitespace-pre-wrap break-words leading-snug`} />;
}

export function ListField({ label, items = [], onChange, disabled, testid, prov, placeholder = "Add item…" }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...(items || []), v.trim()]); setV(""); } };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <span className="flex items-center gap-2"><span className="eyebrow">{label}</span><ProvTag prov={prov} /></span>
      <ul className="space-y-1">
        {(items || []).map((it, i) => (
          <li key={i} className="flex items-start gap-1.5 group">
            <AutoText className={inputCls} value={it} disabled={disabled} data-testid={`${testid}-item-${i}`}
              onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} />
            {!disabled && <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`${testid}-remove-${i}`}><X size={14} /></button>}
          </li>
        ))}
      </ul>
      {!disabled && (
        <div className="flex gap-1.5">
          <AutoText className={inputCls} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} data-testid={`${testid}-new`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <button type="button" onClick={add} className="rounded border border-slate-200 px-2 text-slate-600 hover:bg-slate-50" data-testid={`${testid}-add`}><Plus size={14} /></button>
        </div>
      )}
    </div>
  );
}
